import React, { useRef, useEffect, useCallback } from "react";

interface WaveformVisualizerProps {
  audioElement: HTMLAudioElement | null;
  isPlaying: boolean;
  barCount?: number;
  barWidth?: number;
  barGap?: number;
  minBarHeight?: number;
  maxBarHeight?: number;
  color?: string;
  backgroundColor?: string;
  className?: string;
}

const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({
  audioElement,
  isPlaying,
  barCount = 32,
  barWidth = 3,
  barGap = 2,
  minBarHeight = 4,
  maxBarHeight = 48,
  color = "#fbbf24",
  backgroundColor = "transparent",
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const isInitializedRef = useRef(false);

  const initAnalyzer = useCallback(() => {
    if (!audioElement || isInitializedRef.current) return;

    try {
      audioContextRef.current = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = barCount * 2;
      analyserRef.current.smoothingTimeConstant = 0.8;

      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      try {
        sourceRef.current =
          audioContextRef.current.createMediaElementSource(audioElement);
      } catch (e) {
        console.log("Audio source already connected");
      }

      if (sourceRef.current) {
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
      }

      isInitializedRef.current = true;
    } catch (error) {
      console.error("Error initializing audio analyzer:", error);
    }
  }, [audioElement, barCount]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);

    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    // If we have an analyser and data, use real audio data
    if (analyser && dataArray) {
      if (isPlaying && audioContextRef.current?.state === "suspended") {
        audioContextRef.current.resume();
      }
      analyser.getByteFrequencyData(dataArray);
    }

    const totalWidth = barCount * (barWidth + barGap) - barGap;
    const startX = (width - totalWidth) / 2;
    const centerY = height / 2;

    for (let i = 0; i < barCount; i++) {
      let barHeight = minBarHeight;

      if (analyser && dataArray && isPlaying) {
        const dataIndex = Math.floor(i * (dataArray.length / barCount));
        const value = dataArray[dataIndex];
        const percent = value / 255;
        barHeight = minBarHeight + percent * (maxBarHeight - minBarHeight);
      } else if (isPlaying) {
        // Fallback animation for external sources (Mixcloud etc)
        // Faster, more active animation when playing
        barHeight =
          minBarHeight +
          (Math.sin(Date.now() / 200 + i * 0.4) + 1) * (maxBarHeight / 3);
      } else {
        // Tamed: Completely flat when not playing
        barHeight = minBarHeight;
      }

      const x = startX + i * (barWidth + barGap);
      const y = centerY - barHeight / 2;

      const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
      gradient.addColorStop(0, color);
      gradient.addColorStop(0.5, `${color}dd`);
      gradient.addColorStop(1, `${color}88`);

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
      ctx.fill();
    }

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(draw);
    }
  }, [
    isPlaying,
    barCount,
    barWidth,
    barGap,
    minBarHeight,
    maxBarHeight,
    color,
  ]);

  useEffect(() => {
    if (isPlaying && !isInitializedRef.current) {
      initAnalyzer();
    }
  }, [isPlaying, initAnalyzer]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(draw);
    } else {
      // Draw one flat frame to reset visualizer
      draw();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, draw]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
      isInitializedRef.current = false;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={barCount * (barWidth + barGap)}
      height={maxBarHeight}
      className={className}
      style={{ backgroundColor }}
    />
  );
};

export default WaveformVisualizer;
